import pptxgen from "pptxgenjs";

// ─── Types ───────────────────────────────────────────────────────

export type SlideElement =
  | { kind: "bullet"; text: string; level: number; bold?: boolean; italic?: boolean }
  | { kind: "paragraph"; text: string; bold?: boolean; italic?: boolean }
  | { kind: "subheading"; text: string }
  | { kind: "table"; rows: string[][]; hasHeader: boolean };

export interface SlideData {
  title: string;
  elements: SlideElement[];
}

// ─── Theme constants ─────────────────────────────────────────────

const THEME = {
  bg: "FFFFFF",
  title: "1A1A1A",
  body: "2D2D2D",
  accent: "F97316",
  muted: "6B7280",
  tableHeaderBg: "37474F",
  tableHeaderText: "FFFFFF",
  tableAltRow: "F8F8FA",
  tableBorder: "DCDCE0",
};

const TITLE_FONT = "Calibri";
const BODY_FONT = "Calibri";

// ─── Public API ──────────────────────────────────────────────────

/**
 * Parses a markdown string into slides.
 * Each `## Heading` starts a new slide; everything under it is the slide body.
 * If no `## Heading` is present, the whole content becomes one slide.
 * An optional leading `# Title` becomes a dedicated title slide.
 */
export function parseMarkdownToSlides(markdown: string): SlideData[] {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const slides: SlideData[] = [];

  let i = 0;
  let presentationTitle: string | null = null;

  // Optional leading H1 → title slide
  while (i < lines.length && lines[i].trim() === "") i++;
  if (i < lines.length) {
    const m = lines[i].match(/^#\s+(.+)$/);
    if (m) {
      presentationTitle = m[1].trim();
      i++;
    }
  }

  let current: SlideData | null = presentationTitle
    ? { title: presentationTitle, elements: [] }
    : null;

  const flush = () => {
    if (current) {
      slides.push(current);
      current = null;
    }
  };

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();

    // New slide on ## heading
    const h2 = line.match(/^##\s+(.+)$/);
    if (h2) {
      flush();
      current = { title: h2[1].trim(), elements: [] };
      i++;
      continue;
    }

    // Subheading inside a slide
    const h3 = line.match(/^###\s+(.+)$/);
    if (h3) {
      if (!current) current = { title: "", elements: [] };
      current.elements.push({ kind: "subheading", text: h3[1].trim() });
      i++;
      continue;
    }

    // Tables
    if (line.startsWith("|") && line.endsWith("|") && i + 1 < lines.length) {
      const sepCandidate = lines[i + 1].trim();
      if (/^\|[\s\-:|]+\|$/.test(sepCandidate)) {
        const headerCells = parsePipeLine(line);
        i += 2;
        const rows: string[][] = [headerCells];
        while (i < lines.length) {
          const rl = lines[i].trim();
          if (rl.startsWith("|") && rl.endsWith("|") && !/^\|[\s\-:|]+\|$/.test(rl)) {
            rows.push(parsePipeLine(rl));
            i++;
          } else {
            break;
          }
        }
        if (!current) current = { title: "", elements: [] };
        current.elements.push({ kind: "table", rows, hasHeader: true });
        continue;
      }
    }

    // Bullet list (-, *, +) with indentation-based nesting
    const bulletMatch = raw.match(/^(\s*)[-*+]\s+(.+)$/);
    if (bulletMatch) {
      const indent = bulletMatch[1].length;
      const level = Math.min(Math.floor(indent / 2), 4);
      const { text, bold, italic } = stripInlineFormatting(bulletMatch[2]);
      if (!current) current = { title: "", elements: [] };
      current.elements.push({ kind: "bullet", text, level, bold, italic });
      i++;
      continue;
    }

    // Numbered list item
    const numMatch = raw.match(/^(\s*)\d+\.\s+(.+)$/);
    if (numMatch) {
      const indent = numMatch[1].length;
      const level = Math.min(Math.floor(indent / 2), 4);
      const { text, bold, italic } = stripInlineFormatting(numMatch[2]);
      if (!current) current = { title: "", elements: [] };
      current.elements.push({ kind: "bullet", text, level, bold, italic });
      i++;
      continue;
    }

    // Blank line
    if (line === "") {
      i++;
      continue;
    }

    // Plain paragraph
    const { text, bold, italic } = stripInlineFormatting(line);
    if (!current) current = { title: "", elements: [] };
    current.elements.push({ kind: "paragraph", text, bold, italic });
    i++;
  }

  flush();

  if (slides.length === 0) {
    slides.push({
      title: presentationTitle || "Presentation",
      elements: [{ kind: "paragraph", text: markdown.trim() }],
    });
  }

  return slides;
}

function parsePipeLine(line: string): string[] {
  return line
    .slice(1, -1)
    .split("|")
    .map((c) => stripInlineFormatting(c.trim()).text);
}

function stripInlineFormatting(text: string): {
  text: string;
  bold?: boolean;
  italic?: boolean;
} {
  const bold = /\*\*(.+?)\*\*/.test(text) || /__(.+?)__/.test(text);
  const italic =
    /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/.test(text) ||
    /(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/.test(text);

  const stripped = text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

  return {
    text: stripped,
    ...(bold && { bold: true }),
    ...(italic && { italic: true }),
  };
}

// ─── PPTX rendering ──────────────────────────────────────────────

/**
 * Converts a markdown string into a PPTX Blob ready for download.
 */
export async function buildPptxBlob(markdown: string): Promise<Blob> {
  const slides = parseMarkdownToSlides(markdown);
  return buildPptxBlobFromSlides(slides);
}

/**
 * Converts pre-parsed slides into a PPTX Blob.
 */
export async function buildPptxBlobFromSlides(slides: SlideData[]): Promise<Blob> {
  const pres = new pptxgen();
  pres.layout = "LAYOUT_WIDE"; // 13.333 x 7.5 in
  pres.theme = { headFontFace: TITLE_FONT, bodyFontFace: BODY_FONT };

  const SLIDE_W = 13.333;
  const SLIDE_H = 7.5;
  const MARGIN_X = 0.6;
  const TITLE_Y = 0.45;
  const TITLE_H = 0.9;
  const BODY_Y = TITLE_Y + TITLE_H + 0.15;
  const BODY_H = SLIDE_H - BODY_Y - 0.5;

  slides.forEach((slide) => {
    const s = pres.addSlide();
    s.background = { color: THEME.bg };

    // Accent bar under title (full content width)
    s.addShape("rect", {
      x: MARGIN_X,
      y: TITLE_Y + TITLE_H,
      w: SLIDE_W - MARGIN_X * 2,
      h: 0.04,
      fill: { color: THEME.accent },
      line: { type: "none" },
    });

    // Title
    if (slide.title) {
      s.addText(slide.title, {
        x: MARGIN_X,
        y: TITLE_Y,
        w: SLIDE_W - MARGIN_X * 2,
        h: TITLE_H,
        fontFace: TITLE_FONT,
        fontSize: 32,
        bold: true,
        color: THEME.title,
        valign: "middle",
      });
    }

    // Body
    renderSlideBody(s, slide.elements, MARGIN_X, BODY_Y, SLIDE_W - MARGIN_X * 2, BODY_H);
  });

  const blob = (await pres.write({ outputType: "blob" })) as Blob;
  return blob;
}

function renderSlideBody(
  slide: pptxgen.Slide,
  elements: SlideElement[],
  x: number,
  y: number,
  w: number,
  h: number
) {
  // Separate tables from text/bullets — tables are placed individually below the text block
  const textElements: TextOnlyElement[] = [];
  const tables: Extract<SlideElement, { kind: "table" }>[] = [];

  elements.forEach((el) => {
    if (el.kind === "table") tables.push(el);
    else textElements.push(el);
  });

  let cursorY = y;
  const remainingH = h;

  if (textElements.length > 0) {
    const textBlockH = tables.length > 0 ? Math.min(remainingH * 0.55, 3.5) : remainingH;
    const textRuns = textElements.map((el) => textElementToTextProp(el));
    slide.addText(textRuns, {
      x,
      y: cursorY,
      w,
      h: textBlockH,
      fontFace: BODY_FONT,
      fontSize: 18,
      color: THEME.body,
      valign: "top",
    });
    cursorY += textBlockH + 0.15;
  }

  tables.forEach((tableEl, idx) => {
    const availableH = y + h - cursorY;
    const remainingTables = tables.length - idx;
    const tableH = Math.max(0.8, availableH / remainingTables - 0.15);
    renderTable(slide, tableEl.rows, tableEl.hasHeader, x, cursorY, w, tableH);
    cursorY += tableH + 0.15;
  });
}

type TextOnlyElement = Exclude<SlideElement, { kind: "table" }>;

function textElementToTextProp(el: TextOnlyElement): pptxgen.TextProps {
  if (el.kind === "subheading") {
    return {
      text: el.text,
      options: {
        bold: true,
        fontSize: 22,
        color: THEME.accent,
        paraSpaceBefore: 8,
        paraSpaceAfter: 4,
      },
    };
  }
  if (el.kind === "bullet") {
    return {
      text: el.text,
      options: {
        bullet: { type: "bullet" },
        indentLevel: el.level,
        bold: el.bold,
        italic: el.italic,
        paraSpaceAfter: 4,
      },
    };
  }
  // paragraph
  return {
    text: el.text,
    options: {
      bold: el.bold,
      italic: el.italic,
      paraSpaceAfter: 6,
    },
  };
}

function renderTable(
  slide: pptxgen.Slide,
  rows: string[][],
  hasHeader: boolean,
  x: number,
  y: number,
  w: number,
  h: number
) {
  const tableRows: pptxgen.TableRow[] = rows.map((row, rowIdx) => {
    const isHeader = hasHeader && rowIdx === 0;
    return row.map<pptxgen.TableCell>((cellText) => ({
      text: cellText,
      options: {
        bold: isHeader,
        color: isHeader ? THEME.tableHeaderText : THEME.body,
        fill: { color: isHeader ? THEME.tableHeaderBg : rowIdx % 2 === 0 ? THEME.tableAltRow : THEME.bg },
        align: "left",
        valign: "middle",
        fontFace: BODY_FONT,
        fontSize: isHeader ? 13 : 12,
      },
    }));
  });

  slide.addTable(tableRows, {
    x,
    y,
    w,
    h,
    border: { type: "solid", pt: 0.5, color: THEME.tableBorder },
    fontFace: BODY_FONT,
    fontSize: 12,
    color: THEME.body,
  });
}
